import { CommonModule, Location } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output, Signal, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { PatientProvider } from '../../../../care-team/models/patient-provider';
import { getPatientCareTeam } from '../../../../care-team/store/actions/care-team.actions';
import { selectCareTeamMemberById } from '../../../../care-team/store/selectors/care-team.selector';
import { loadPageWithPatientIdParameter } from '../../../../core/store/actions/app.actions';
import { selectIsMobileBrowser } from '../../../../core/store/selectors/app.selector';
import { IconComponent } from '../../../../shared/components/icon/icon.component';
import { PageHeaderComponent } from '../../../../shared/components/page-header/page-header.component';
import { ProgressSpinnerComponent } from '../../../../shared/components/progress-spinner/progress-spinner.component';
import { visitTypes } from '../../../data/visit-type';
import { VisitType } from '../../../models/visit-type';

@Component({
  selector: 'app-visit-type-selection',
  imports: [CommonModule, LetDirective, ProgressSpinnerComponent, IconComponent, MatButtonModule, PageHeaderComponent],
  templateUrl: './visit-type-selection.component.html',
  styleUrl: './visit-type-selection.component.scss',
})
export class VisitTypeSelectionComponent implements OnInit {
  private store = inject(Store);
  private location = inject(Location);
  providerId!: string;
  patientCareTeamMember!: Signal<PatientProvider | null>;
  @Input() set providerUserId(value: string) {
    this.providerId = value;
    this.patientCareTeamMember$ = this.store.select(selectCareTeamMemberById(this.providerId));
    this.patientCareTeamMember = this.store.selectSignal(selectCareTeamMemberById(this.providerId));
  }
  @Input() isModal: boolean = false;
  @Output() visitTypeSelected = new EventEmitter<{ value: number; isVirtualVisitType: boolean }>();

  patientCareTeamMember$!: Observable<PatientProvider | null>;
  visitTypes = visitTypes;
  selectedVisitTypeId = signal<number | null>(null);

  isMobileBrowser = this.store.selectSignal(selectIsMobileBrowser);

  ngOnInit(): void {
    this.store.dispatch(getPatientCareTeam());
  }

  onVisitTypeActivate(visitType: VisitType, patientCareTeamMember: PatientProvider) {
    if (this.isMobileBrowser()) {
      this.selectedVisitTypeId.set(visitType.id);
      return;
    }

    this.completeSelection(visitType, patientCareTeamMember);
  }

  onRowKeydown(event: KeyboardEvent, visitType: VisitType, patientCareTeamMember: PatientProvider) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onVisitTypeActivate(visitType, patientCareTeamMember);
    }
  }

  onCancel() {
    this.selectedVisitTypeId.set(null);
    this.location.back();
  }

  onContinue() {
    const patientCareTeamMember = this.patientCareTeamMember?.();
    if (!patientCareTeamMember) return;

    const selectedVisitTypeId = this.selectedVisitTypeId();
    if (!selectedVisitTypeId) return;

    const selectedVisitType = this.visitTypes.find(visitType => visitType.id === selectedVisitTypeId);
    if (!selectedVisitType) return;

    this.completeSelection(selectedVisitType, patientCareTeamMember);
  }

  getVisitTypeIcon(visitType: VisitType): string {
    if (!visitType.isLocationSpecific) return 'video_call';

    switch (visitType.id) {
      case 1:
        return 'calendar_month';
      case 2:
        return 'event_available';
      case 3:
        return 'stethoscope';
      default:
        return 'event_available';
    }
  }

  getVisitTypeAccent(visitType: VisitType): string {
    switch (visitType.id) {
      case 1:
        return '#2563eb';
      case 2:
        return '#7c3aed';
      case 3:
        return '#0891b2';
      default:
        return '#64748b';
    }
  }

  private completeSelection(visitType: VisitType, patientCareTeamMember: PatientProvider) {
    if (this.isMobileBrowser()) {
      if (visitType.isLocationSpecific) {
        this.store.dispatch(
          loadPageWithPatientIdParameter({
            route: `appointments/schedule/${this.providerId}/${visitType.id}/provider-location`,
          })
        );
        return;
      }

      const teamId = patientCareTeamMember.teams[0]?.teamId;
      if (!teamId) return;

      this.store.dispatch(
        loadPageWithPatientIdParameter({
          route: `appointments/schedule/${this.providerId}/${visitType.id}/${teamId}/appointment-selection`,
        })
      );
      return;
    }

    this.visitTypeSelected.emit({ value: visitType.id, isVirtualVisitType: !visitType.isLocationSpecific });
  }
}
